import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Button, Alert, IconButton, TextField, MenuItem,
  Tooltip, Grid, Switch, FormControlLabel, Divider, CircularProgress,
} from '@mui/material';
import { Home, Add, Delete, Save, AccountTree } from '@mui/icons-material';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const ApprovalWorkflowSetup = () => {
  const navigate = useNavigate();
  const [types,      setTypes]      = useState([]);   // [{documentType,label}]
  const [docType,    setDocType]    = useState('');
  const [enabled,    setEnabled]    = useState(false);
  const [approvers,  setApprovers]  = useState([]);   // [{sequence,approverType,approverId,approverName}]
  const [users,      setUsers]      = useState([]);
  const [groups,     setGroups]     = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState('');
  const [success,    setSuccess]    = useState('');

  // Load document types + user/group pickers once.
  useEffect(() => {
    (async () => {
      try {
        const [tRes, uRes, gRes] = await Promise.all([
          axios.get(`${API}/api/approvals/setup/meta/types`, { headers: hdrs() }),
          axios.get(`${API}/api/users`,       { headers: hdrs() }),
          axios.get(`${API}/api/user-groups`, { headers: hdrs() }),
        ]);
        setTypes(tRes.data);
        setUsers(uRes.data);
        setGroups(gRes.data);
        if (tRes.data.length) setDocType(tRes.data[0].documentType);
      } catch { setError('Failed to load approval setup'); }
      finally { setLoading(false); }
    })();
  }, []);

  const loadSetup = useCallback(async (dt) => {
    if (!dt) return;
    setError(''); setSuccess('');
    try {
      const { data } = await axios.get(`${API}/api/approvals/setup/${dt}`, { headers: hdrs() });
      setEnabled(!!data.enabled);
      setApprovers((data.approvers || []).map(a => ({ ...a })));
    } catch { setError('Failed to load workflow'); }
  }, []);

  useEffect(() => { loadSetup(docType); }, [docType, loadSetup]);

  const addApprover = () => {
    const nextSeq = approvers.length ? Math.max(...approvers.map(a => a.sequence || 1)) + 1 : 1;
    setApprovers(a => [...a, { sequence: nextSeq, approverType: 'User', approverId: '', approverName: '' }]);
  };

  const updateApprover = (idx, patch) => {
    setApprovers(list => list.map((a, i) => i === idx ? { ...a, ...patch } : a));
  };

  const removeApprover = (idx) => setApprovers(list => list.filter((_, i) => i !== idx));

  // When picking an approver, capture a display name too.
  const pickApprover = (idx, type, id) => {
    let name = '';
    if (type === 'User') {
      const u = users.find(x => x._id === id);
      name = u ? (u.fullName || u.email) : '';
    } else {
      const g = groups.find(x => x.code === id);
      name = g ? g.description || g.code : '';
    }
    updateApprover(idx, { approverType: type, approverId: id, approverName: name });
  };

  const handleSave = async () => {
    setError(''); setSuccess('');
    if (enabled && approvers.length === 0) { setError('Add at least one approver before enabling'); return; }
    if (approvers.some(a => !a.approverId)) { setError('Every approver row needs a user or group selected'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/api/approvals/setup/${docType}`, { enabled, approvers }, { headers: hdrs() });
      setSuccess('Workflow saved');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  if (loading) return (
    <Box sx={{ maxWidth: 900, mx: 'auto', mt: 6, textAlign: 'center' }}><CircularProgress /></Box>
  );

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', mt: 4, mb: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <AccountTree color="primary" />
              <Typography variant="h5" color="primary">Approval Workflows</Typography>
            </Box>
            <Tooltip title="Back to Setups">
              <IconButton onClick={() => navigate('/setups')} sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}>
                <Home />
              </IconButton>
            </Tooltip>
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            Configure who must approve a document before it can be processed. Approvers with the <strong>same
            sequence</strong> form one step where any one of them can approve; different sequences run in order
            (1 → 2 → 3). Approver can be a specific user or a user group (any member of the group can act).
          </Alert>

          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}

          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <TextField select label="Document Type" value={docType} fullWidth size="small"
                onChange={e => setDocType(e.target.value)}>
                {types.map(t => <MenuItem key={t.documentType} value={t.documentType}>{t.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={<Switch checked={enabled} onChange={e => setEnabled(e.target.checked)} />}
                label={enabled ? 'Approval required (enabled)' : 'Approval not required (disabled)'}
              />
            </Grid>
          </Grid>

          <Divider sx={{ mb: 2 }} />

          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="subtitle1" fontWeight={700}>Approvers</Typography>
            <Button size="small" startIcon={<Add />} onClick={addApprover}>Add Approver</Button>
          </Box>

          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell width={90}><strong>Sequence</strong></TableCell>
                <TableCell width={140}><strong>Type</strong></TableCell>
                <TableCell><strong>Approver</strong></TableCell>
                <TableCell align="right" width={60}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {approvers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body2" color="text.secondary" py={2}>
                      No approvers yet. Click "Add Approver" to build the chain.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : approvers.map((a, idx) => (
                <TableRow key={idx} hover>
                  <TableCell>
                    <TextField type="number" size="small" value={a.sequence}
                      onChange={e => updateApprover(idx, { sequence: Number(e.target.value) || 1 })}
                      inputProps={{ min: 1, style: { width: 60 } }} />
                  </TableCell>
                  <TableCell>
                    <TextField select size="small" fullWidth value={a.approverType}
                      onChange={e => pickApprover(idx, e.target.value, '')}>
                      <MenuItem value="User">User</MenuItem>
                      <MenuItem value="UserGroup">User Group</MenuItem>
                    </TextField>
                  </TableCell>
                  <TableCell>
                    {a.approverType === 'User' ? (
                      <TextField select size="small" fullWidth value={a.approverId}
                        onChange={e => pickApprover(idx, 'User', e.target.value)}>
                        <MenuItem value=""><em>— Select user —</em></MenuItem>
                        {users.map(u => (
                          <MenuItem key={u._id} value={u._id}>{u.fullName || u.email} ({u.email})</MenuItem>
                        ))}
                      </TextField>
                    ) : (
                      <TextField select size="small" fullWidth value={a.approverId}
                        onChange={e => pickApprover(idx, 'UserGroup', e.target.value)}>
                        <MenuItem value=""><em>— Select group —</em></MenuItem>
                        {groups.map(g => (
                          <MenuItem key={g.code} value={g.code}>{g.code} — {g.description}</MenuItem>
                        ))}
                      </TextField>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Remove">
                      <IconButton size="small" color="error" onClick={() => removeApprover(idx)}><Delete fontSize="small" /></IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Button variant="contained" startIcon={<Save />} onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save Workflow'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ApprovalWorkflowSetup;
